/**
 * Bitamin Naturals — Shopify Image Optimizer
 * Upload to: Online Store > Themes > Assets > image-optimizer.js
 * Load from theme.liquid before </body>:
 *   <script src="{{ 'image-optimizer.js' | asset_url }}" defer></script>
 *
 * What it does:
 * - Rewrites Shopify CDN image URLs to serve correctly-sized versions
 * - Adds proper srcset to images missing it
 * - Fixes oversized images (800px served at 269px display size etc)
 * - Sets loading="lazy" on all below-fold images automatically
 * - Uses IntersectionObserver so images only load when near viewport
 */

(function () {
  'use strict';

  var SHOPIFY_CDN_PATTERN = /\/(cdn\/shop\/(files|collections|products)[^"'\s]*)/;
  var WIDTH_PARAM = /[?&]width=\d+/;

  var SRCSET_WIDTHS = [165, 360, 535, 750, 1100];
  var COLLECTION_WIDTHS = [165, 269, 360, 535];
  var HERO_WIDTHS = [400, 600, 900, 1200, 1600, 1920];

  function getShopifyImageBase(src) {
    if (!src) return null;
    src = src.replace(/^\/\//, 'https://');
    if (
      src.indexOf('bitaminnaturals.com/cdn/') === -1 &&
      src.indexOf('cdn.shopify.com') === -1
    ) return null;
    return src.replace(WIDTH_PARAM, '');
  }

  function buildSrcsetUrl(base, width) {
    var sep = base.indexOf('?') !== -1 ? '&' : '?';
    return base + sep + 'width=' + width;
  }

  function buildSrcset(base, widths) {
    return widths.map(function (w) {
      return buildSrcsetUrl(base, w) + ' ' + w + 'w';
    }).join(', ');
  }

  function getDisplayWidth(img) {
    var rect = img.getBoundingClientRect();
    return Math.round(rect.width) || img.offsetWidth || 400;
  }

  function getBestWidth(displayWidth, widths) {
    var dpr = window.devicePixelRatio || 1;
    var target = displayWidth * Math.min(dpr, 2);
    for (var i = 0; i < widths.length; i++) {
      if (widths[i] >= target) return widths[i];
    }
    return widths[widths.length - 1];
  }

  function isHeroImage(img) {
    return (
      img.fetchPriority === 'high' ||
      img.getAttribute('fetchpriority') === 'high' ||
      img.classList.toString().indexOf('banner') !== -1 ||
      img.classList.toString().indexOf('hero') !== -1 ||
      img.classList.toString().indexOf('slideshow') !== -1
    );
  }

  function isCollectionImage(img) {
    var src = img.src || '';
    return (
      src.indexOf('/collections/') !== -1 ||
      img.classList.toString().indexOf('collection') !== -1
    );
  }

  function optimizeImage(img) {
    var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
    var base = getShopifyImageBase(src);
    if (!base) return;

    var displayWidth = getDisplayWidth(img);
    var widths = isHeroImage(img)
      ? HERO_WIDTHS
      : isCollectionImage(img)
      ? COLLECTION_WIDTHS
      : SRCSET_WIDTHS;

    var bestWidth = getBestWidth(displayWidth, widths);
    var newSrc = buildSrcsetUrl(base, bestWidth);
    var newSrcset = buildSrcset(base, widths);

    var currentSrcWidth = parseInt((src.match(/[?&]width=(\d+)/) || [])[1] || 0);
    var isOversized = currentSrcWidth > bestWidth * 1.3;

    if (!img.srcset || isOversized) {
      img.srcset = newSrcset;
    }

    if (isOversized && img.src !== newSrc) {
      img.src = newSrc;
    }

    if (!img.sizes) {
      if (isHeroImage(img)) {
        img.sizes = '100vw';
      } else if (isCollectionImage(img)) {
        img.sizes = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
      } else {
        img.sizes = '(min-width: 990px) 33vw, (min-width: 750px) 50vw, 100vw';
      }
    }

    if (!img.width && !img.height) {
      var naturalRatio = img.naturalWidth && img.naturalHeight
        ? img.naturalHeight / img.naturalWidth
        : 1;
      img.width = bestWidth;
      img.height = Math.round(bestWidth * naturalRatio);
    }
  }

  function setLazyLoading() {
    var viewportHeight = window.innerHeight;
    var images = document.querySelectorAll('img');
    images.forEach(function (img) {
      if (isHeroImage(img)) {
        img.loading = 'eager';
        return;
      }
      var rect = img.getBoundingClientRect();
      if (rect.top > viewportHeight * 1.5) {
        img.loading = 'lazy';
      }
    });
  }

  function optimizeAllImages() {
    var images = document.querySelectorAll('img[src*="cdn/shop"], img[src*="cdn.shopify"]');
    images.forEach(function (img) {
      if (!isHeroImage(img)) {
        optimizeImage(img);
      }
    });
  }

  function setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          optimizeImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '300px 0px' });

    var lazyImages = document.querySelectorAll(
      'img[loading="lazy"][src*="cdn/shop"], img[loading="lazy"][src*="cdn.shopify"]'
    );
    lazyImages.forEach(function (img) {
      observer.observe(img);
    });
  }

  function watchForNewImages() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var imgs = node.tagName === 'IMG'
            ? [node]
            : Array.from(node.querySelectorAll ? node.querySelectorAll('img') : []);
          imgs.forEach(function (img) {
            if (!isHeroImage(img)) optimizeImage(img);
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    setLazyLoading();
    optimizeAllImages();
    setupIntersectionObserver();
    watchForNewImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();